import { useMemo } from 'react';
import { useConversation, useConversationsAttributes } from '@/entities/conversations';
import { useAttributeGroups } from '@/entities/attributes';
import type { ConversationAttributeItem } from '@/entities/attributes';
import { sortAttributeGroups } from '../lib';
import {
  buildGroupsWithAttributes,
  buildGroupsWithValues,
  type GroupWithAttributes,
} from '../lib/dialog-data-derived';

type ConversationData = ReturnType<typeof useConversation>['data'];
type ExpertReportItem = NonNullable<NonNullable<ConversationData>['expertReports']>[number];

/**
 * Результат хука `useDialogDataDerived`.
 */
type UseDialogDataDerivedResult = {
  /** Группы атрибутов, у которых есть хотя бы одно заполненное значение в диалоге. */
  groupsWithValues: GroupWithAttributes[];
  /** Плоский список групп, видимых в табах (код + имя). */
  visibleGroups: Array<{ code: string; name: string }>;
  /**
   * Дедуплицированный список всех атрибутов из всех видимых групп.
   * Используется для таба «Все атрибуты».
   */
  allGroupAttributes: ConversationAttributeItem[];
  /** Экспертные отчёты со статусом DONE. */
  doneExpertReports: ExpertReportItem[];
  /** Данные диалога из API. */
  conversationData: ConversationData;
  /** true, пока хотя бы один из запросов загружается. */
  isLoading: boolean;
};

/**
 * Вычисляет производные данные для секции данных диалога:
 * группы атрибутов с заполненными значениями, дедуплицированный список атрибутов
 * для таба «Все атрибуты» и завершённые экспертные отчёты.
 */
export function useDialogDataDerived(dialogId: string): UseDialogDataDerivedResult {
  const { data: conversationData, isLoading: isConversationLoading } = useConversation(dialogId);
  const { data: groupsData, isLoading: isGroupsLoading } = useAttributeGroups({ limit: 100 });
  const { data: attributesData, isLoading: isAttributesLoading } = useConversationsAttributes({
    limit: 1000,
  });

  const sortedGroups = useMemo(
    () => sortAttributeGroups(groupsData?.attributeGroups ?? []),
    [groupsData?.attributeGroups],
  );

  const groupsWithAttributes = useMemo(() => {
    if (!attributesData?.attributes) return [] as GroupWithAttributes[];
    return buildGroupsWithAttributes(sortedGroups, attributesData.attributes);
  }, [sortedGroups, attributesData?.attributes]);

  const groupsWithValues = useMemo(
    () => buildGroupsWithValues(groupsWithAttributes, conversationData?.conversationAttrValues),
    [groupsWithAttributes, conversationData?.conversationAttrValues],
  );

  const visibleGroups = useMemo(() => groupsWithValues.map(({ group }) => group), [groupsWithValues]);

  const allGroupAttributes = useMemo(() => {
    const seen = new Set<string>();
    const result: ConversationAttributeItem[] = [];
    for (const { attributes } of groupsWithValues) {
      for (const attr of attributes) {
        if (!seen.has(attr.path)) {
          seen.add(attr.path);
          result.push(attr);
        }
      }
    }
    return result;
  }, [groupsWithValues]);

  const doneExpertReports = useMemo(
    () => (conversationData?.expertReports ?? []).filter((er) => er.status === 'DONE'),
    [conversationData?.expertReports],
  );

  const isLoading = isConversationLoading || isGroupsLoading || isAttributesLoading;

  return {
    groupsWithValues,
    visibleGroups,
    allGroupAttributes,
    doneExpertReports,
    conversationData,
    isLoading,
  };
}
